/**
 * Extracts product identifiers needed for intent resolution from product data.
 *
 * @param {Object} product - The product object containing identifiers
 * @returns {Object} An object containing omsId and sku for intent resolution
 * @returns {string} returns.omsId - The OMS item ID from product identifiers
 * @returns {string} returns.sku - The SKU (storeSkuNumber, omsThdSku, or specialOrderSku)
 */
export const extractProductIdentifiersForIntent = (product) => {
  if (!product?.identifiers) {
    return { omsId: null, sku: null };
  }

  const { identifiers } = product;
  const omsId = identifiers.itemId;
  const sku = identifiers.storeSkuNumber || identifiers.omsThdSku || identifiers.specialOrderSku;

  return { omsId, sku };
};

/**
 * Resolves intents from items by mapping itemIds from intentItemMap to groupedMaterials.
 *
 * @param {Array} groupedMaterials - Array of group objects containing materials with intent information
 * @param {Object} intentItemMap - Map of intent identifiers to objects containing:
 * @param {Array<string>} intentItemMap[key].itemIds - Array of item IDs for the intent
 * @param {string} intentItemMap[key].sku - SKU for the intent
 * @param {string} intentItemMap[key].omsId - OMS ID for the intent
 * @param {boolean} isProjects - Flag indicating if the context is projects, which determines
 * if sku and omsId should be included in the resolved materials
 * @returns {Array} groupedMaterials with itemId, sku, and omsId added for matching intents
 */
export const resolveIntentFromItem = (groupedMaterials, intentItemMap, isProjects) => {
  if (!groupedMaterials || !intentItemMap) {
    return groupedMaterials;
  }

  return groupedMaterials.map((group) => {
    let itemOrder = 0;
    const resolvedMaterials = group.materials?.flatMap((material) => {
      const intentKey = material.intent || material.intentId;
      const itemIds = intentItemMap[intentKey]?.itemIds;

      if (itemIds?.length) {
        return itemIds.map((itemId) => {
          const currentOrder = itemOrder;
          itemOrder += 1;
          if (isProjects) {
            return {
              ...material,
              itemId,
              itemOrder: currentOrder,
              sku: intentItemMap[intentKey]?.sku,
              omsId: intentItemMap[intentKey]?.omsId,
              price: intentItemMap[intentKey]?.price ?? null,
            };
          }

          // Returning itemId for non-projects to be used in intents resolution,
          // but not including sku and omsId as they are not needed for non-projects
          return {
            ...material,
            itemId,
            itemOrder: currentOrder,
          };
        });
      }

      return material;
    });

    return { ...group, materials: resolvedMaterials || group.materials };
  });
};

/**
 * Map of purchase intents to product IDs
 * @example
 * {
 *   "Waterproofing membrane": "27843f3c-1801-462e-8b53-ccbd199d535e",
 *   "Cement board": "3359c3b1-e759-4dee-befc-7be23ed3483d"
 * }
 */
export const getIntentWithIdMap = (itemGroups) => {
  return (itemGroups ?? [])?.flatMap((group) => (group?.products || [])
    .filter((product) => product.purchaseIntent)
    .map((product) => ({ [product.purchaseIntent]: product?.id }))
  ).reduce((acc, obj) => ({ ...acc, ...obj }), {});
};

export const getItemsToResolveWithMissingData = (currentIntents) => {
  return currentIntents.flatMap((intent) => intent.materials
    .filter((material) => !material.itemId || !material.omsId || !material.sku)
  );
};

/**
 * Returns an array of items to resolve for bulk intents.
 * @param {Array} currentIntents - Array of intent objects containing materials
 * @param {Object} intentsIds - Map of intent identifiers to product IDs
 * @returns {Array<Object>} Array of material objects
 * @example
 * [
 *   {
 *     purchaseIntent: "Waterproofing membrane",
 *     itemType: "catalog",
 *     quantity: 2,
 *     sku: "12345",
 *     itemId: "27843f3c-1801-462e-8b53-ccbd199d535e",
 *     omsId: "67890"
 *   }
 * ]
 */
export const itemsResolveBulkIntents = (currentIntents, intentsIds) => {
  return currentIntents.flatMap((intent) => intent.materials
    .filter((material) => material.itemId && material.omsId && material.sku)
    .map((material) => ({
      purchaseIntent: material.intent || material.intentId,
      itemType: 'catalog',
      quantity: material.quantity,
      sku: material.sku,
      itemId: intentsIds[material.intent] ?? null,
      omsId: material.omsId
    }))
  );
};

/**
 * Formats selected materials into analytics items for tracking.
 * @param {Array} selectedMaterials - Array of group objects containing materials
 * @param {Object} options - Configuration options
 * @param {string} options.sourceType - Source type of the materials (e.g., 'ai')
 * @param {string} options.sessionId - Session ID for AI tracking
 * @param {string} options.aiInputMethod - AI input method used
 * @param {string} options.aiInputType - AI input type used
 * @returns {Array<Object>} Array of formatted analytics items
 * @example
 * [
 *   {
 *     quantity: 1,
 *     price: 59.88,
 *     sku: "1000110421",
 *     intentStatus: "yes",
 *     resolvedFromIntent: "yes",
 *     aiSessionId: "abc123",
 *     listStarter: "build with ai: voice: description"
 *   }
 * ]
 */
export const formatAnalyticsItemsFromMaterials = (selectedMaterials, options = {}) => {
  const { sourceType, sessionId, aiInputMethod, aiInputType } = options;

  return (selectedMaterials || []).flatMap((group) => (group.materials || []).map((material) => {
    const formattedItem = {
      quantity: material.quantity || 1,
      // If itemId exists, it means that the selected materials were resolved by `QuickIntentResolve`
      price: material?.itemId ? material.price || 0 : 0,
      sku: material?.itemId ? material.sku : material.id,
      intentStatus: material?.itemId ? 'yes' : 'no',
      // If itemId exists, it means that the selected materials were resolved by `QuickIntentResolve`
      resolvedFromIntent: material?.itemId ? 'yes' : 'no',
      aiQuantityEstimate: material?.aiQuantityEstimate ? 'yes' : 'no',
      aiSessionId: sourceType === 'ai' ? sessionId : '',
    };

    if (sourceType === 'ai') {
      formattedItem.listStarter = `build with ai: ${aiInputMethod}: ${aiInputType}`;
    }

    return formattedItem;
  }));
};

export const getAddToListAddActionForProjectsAnalytics = ({
  selectedMaterials, sourceType, sessionId, aiInputMethod, aiInputType, starterListName, projectId, isQuickAutoResolve
}) => {
  // If quick auto-resolve, then calculate the value, if not, return an empty string.
  const projectAddTotal = !isQuickAutoResolve ? '' : (selectedMaterials || [])
    .flatMap((group) => group.materials || [])
    .reduce((total, material) => {
      const price = Number(material.price) || 0;
      const quantity = Number(material.quantity) || 1;
      return total + price * quantity;
    }, 0);
  const uniqueSkuCount = !isQuickAutoResolve ? '' : (selectedMaterials || [])
    .flatMap((group) => group.materials || [])
    .reduce((uniqueSkus, material) => {
      if (material.sku) {
        uniqueSkus.add(material.sku);
      }
      return uniqueSkus;
    }, new Set()).size;

  return {
    name: 'project add',
    location: 'INTENTS_PREVIEW',
    items: formatAnalyticsItemsFromMaterials(selectedMaterials, {
      sourceType,
      sessionId,
      aiInputMethod,
      aiInputType
    }),
    uniqueSkuCount,
    projectAddTotal,
    sourceType,
    sessionId,
    aiInputMethod,
    aiInputType,
    starterListName,
    projectId: projectId ?? ''
  };
};
